export const DRAFT_STATUS = "draft"
export const ARCHIVED_STATUS = "archived"

const doesSupportReview = import.meta.env.VITE_DOES_SUPPORT_REVIEW === "true"

// 劇本狀態：草稿、待審核、審核中、可使用、排程中、執行中、已取消、已結束
const ASSETS_STATE_JOURNEY = {
    DRAFT: "draft",
    AVAILABLE: "available",
    SCHEDULING: "scheduling",
    RUNNING: "running",
    IN_USE: "in_use",
    CANCELED: "canceled",
    ENDED: "ended",
    ARCHIVED: "archived",
    ...(doesSupportReview ? ({ REVIEWABLE: "reviewable", REVIEWING: "reviewing" } as const) : {}),
} as const

export { ASSETS_STATE_JOURNEY }

// 劇本狀態中文：草稿、待審核、審核中、可使用、排程中、執行中、已取消、已結束、使用中
const ASSETS_STATE_JOURNEY_CHINESE: Record<string, string> = {
    [ASSETS_STATE_JOURNEY.DRAFT]: "草稿",
    [ASSETS_STATE_JOURNEY.AVAILABLE]: "可使用",
    [ASSETS_STATE_JOURNEY.SCHEDULING]: "排程中",
    [ASSETS_STATE_JOURNEY.RUNNING]: "執行中",
    [ASSETS_STATE_JOURNEY.IN_USE]: "使用中",
    [ASSETS_STATE_JOURNEY.CANCELED]: "已取消",
    [ASSETS_STATE_JOURNEY.ENDED]: "已結束",
    [ASSETS_STATE_JOURNEY.ARCHIVED]: "已封存",
    ...(doesSupportReview ? { reviewable: "待審核", reviewing: "審核中" } : {}),
}

export { ASSETS_STATE_JOURNEY_CHINESE }

// 劇本顏色：草稿、審核中、待審核、已通過、生效中、已失效
const ASSETS_STATE_JOURNEY_COLOR: Record<string, string> = {
    [ASSETS_STATE_JOURNEY.DRAFT]: "#323232",
    [ASSETS_STATE_JOURNEY.AVAILABLE]: "#14B577",
    [ASSETS_STATE_JOURNEY.ENDED]: "#999",
    ...(doesSupportReview ? { reviewable: "#F18C29", reviewing: "#FFCC02" } : {}),
}

export { ASSETS_STATE_JOURNEY_COLOR }

// 劇本甘特圖顏色：草稿、待審核、審核中、可使用、排程中、執行中、已取消、已結束
const JOURNEY_GANTT_COLOR: Record<string, { color: string; backgroundColor: string; backgroundMask: string }> = {
    [ASSETS_STATE_JOURNEY.DRAFT]: {
        color: "#3D3D3D",
        backgroundColor: "#6D6D6D",
        backgroundMask: "#ffffffd9",
    },
    [ASSETS_STATE_JOURNEY.AVAILABLE]: {
        color: "#317912",
        backgroundColor: "#52C41A",
        backgroundMask: "#ffffffd9",
    },
    [ASSETS_STATE_JOURNEY.SCHEDULING]: {
        color: "#3785FA",
        backgroundColor: "#3785FA",
        backgroundMask: "#fffffff2",
    },
    [ASSETS_STATE_JOURNEY.RUNNING]: {
        color: "#52C41A",
        backgroundColor: "#98EF67",
        backgroundMask: "#ffffffd9",
    },
    [ASSETS_STATE_JOURNEY.CANCELED]: {
        color: "#6D6D6D",
        backgroundColor: "#6D6D6D",
        backgroundMask: "#ffffffe6",
    },
    [ASSETS_STATE_JOURNEY.ENDED]: {
        color: "#B0B0B0",
        backgroundColor: "#6D6D6D",
        backgroundMask: "#fffffff2",
    },
    ...(doesSupportReview
        ? {
              reviewable: {
                  color: "#FF751A",
                  backgroundColor: "#FF751A",
                  backgroundMask: "#fffffff2",
              },
              reviewing: {
                  color: "#FAC400",
                  backgroundColor: "#FAC400",
                  backgroundMask: "#fffffff2",
              },
          }
        : {}),
}

export { JOURNEY_GANTT_COLOR }

// 素材狀態：草稿、待審核、審核中、可使用、已停用
const ASSETS_STATE_ASSET = {
    DRAFT: "draft",
    AVAILABLE: "available",
    IN_USE: "in_use",
    DISABLED: "disabled",
    ARCHIVED: "archived",
    ...(doesSupportReview ? ({ REVIEWABLE: "reviewable", REVIEWING: "reviewing" } as const) : {}),
} as const

export { ASSETS_STATE_ASSET }

// 素材中文：草稿、待審核、審核中、可使用、已停用
const ASSETS_STATE_ASSET_CHINESE: Record<string, string> = {
    [ASSETS_STATE_ASSET.DRAFT]: "草稿",
    [ASSETS_STATE_ASSET.AVAILABLE]: "可使用",
    [ASSETS_STATE_ASSET.IN_USE]: "使用中",
    [ASSETS_STATE_ASSET.DISABLED]: "已停用",
    ...(doesSupportReview ? { reviewable: "待審核", reviewing: "審核中" } : {}),
}

export { ASSETS_STATE_ASSET_CHINESE }

// 劇本素材顏色：草稿、審核中、待審核、已通過、使用中
const ASSETS_STATE_ASSET_COLOR: Record<string, string> = {
    [ASSETS_STATE_ASSET.DRAFT]: "#323232",
    [ASSETS_STATE_ASSET.AVAILABLE]: "#14B577",
    [ASSETS_STATE_ASSET.IN_USE]: "#409EFF",
    ...(doesSupportReview ? { reviewable: "#F18C29", reviewing: "#FFCC02" } : {}),
}

export { ASSETS_STATE_ASSET_COLOR }

// 特殊條件：固定受眾、指定標籤、行銷活動回應、行銷活動日期
export const SPECIAL_CONDITION_TYPE = {
    AUDIENCE: "audience",
    TAG: "tag",
    CAMPAIGN: "campaign",
    CAMPAIGN_DATE: "campaign_date",
} as const

export const SPECIAL_CONDITION_TYPE_CHINESE: Record<string, string> = {
    [SPECIAL_CONDITION_TYPE.AUDIENCE]: "固定受眾",
    [SPECIAL_CONDITION_TYPE.TAG]: "指定標籤",
    [SPECIAL_CONDITION_TYPE.CAMPAIGN]: "行銷活動回應",
    [SPECIAL_CONDITION_TYPE.CAMPAIGN_DATE]: "行銷活動日期",
}

// 行銷活動回應選項：點擊、開信或曝光、寄達、活動名單
export const SPECIAL_CONDITION_CAMPAIGN_OPTION = {
    CLICK: "click",
    OPEN: "open",
    SENT_SUCCESS: "sent_success",
    ALL: "all",
} as const

export const SPECIAL_CONDITION_CAMPAIGN_OPTION_CHINESE: Record<string, string> = {
    [SPECIAL_CONDITION_CAMPAIGN_OPTION.CLICK]: "點擊",
    [SPECIAL_CONDITION_CAMPAIGN_OPTION.OPEN]: "開信或曝光",
    [SPECIAL_CONDITION_CAMPAIGN_OPTION.SENT_SUCCESS]: "寄達",
    [SPECIAL_CONDITION_CAMPAIGN_OPTION.ALL]: "活動名單",
}

// 受眾狀態：草稿、計算中、可使用、使用中、已停用、封存
export const ASSETS_STATE_AUDIENCE = {
    DRAFT: "draft",
    CALCULATING: "calculating",
    AVAILABLE: "available",
    IN_USE: "in_use",
    DISABLE: "disabled",
    ARCHIVED: "archived",
} as const

export const ASSETS_STATE_AUDIENCE_CHINESE: Record<string, string> = {
    [ASSETS_STATE_AUDIENCE.DRAFT]: "草稿",
    [ASSETS_STATE_AUDIENCE.CALCULATING]: "計算中",
    [ASSETS_STATE_AUDIENCE.AVAILABLE]: "可使用",
    [ASSETS_STATE_AUDIENCE.IN_USE]: "使用中",
    [ASSETS_STATE_AUDIENCE.DISABLE]: "已停用",
}

export const ASSETS_STATE_AUDIENCE_COLOR: Record<string, string> = {
    [ASSETS_STATE_AUDIENCE.DRAFT]: "#4F4F4F",
    [ASSETS_STATE_AUDIENCE.CALCULATING]: "#FAC400",
    [ASSETS_STATE_AUDIENCE.AVAILABLE]: "#52C41A",
    [ASSETS_STATE_AUDIENCE.IN_USE]: "#3785FA",
    [ASSETS_STATE_AUDIENCE.DISABLE]: "#FF4747",
}

// 受眾類型：固定受眾、條件受眾
export const ASSETS_AUDIENCE_TYPE = {
    MANUAL: "manual",
    RULE: "rule",
} as const

export const ASSIGNER_TYPE_CHINESE: Record<string, string> = {
    [ASSETS_AUDIENCE_TYPE.MANUAL]: "固定貼標",
    [ASSETS_AUDIENCE_TYPE.RULE]: "條件貼標",
}

export const ASSETS_AUDIENCE_TYPE_CHINESE: Record<string, string> = {
    [ASSETS_AUDIENCE_TYPE.MANUAL]: "固定受眾",
    [ASSETS_AUDIENCE_TYPE.RULE]: "條件受眾",
}

export const ASSETS_AUDIENCE_TYPE_COLOR: Record<string, string> = {
    [ASSETS_AUDIENCE_TYPE.MANUAL]: "#E6F7FF",
    [ASSETS_AUDIENCE_TYPE.RULE]: "#FFFAE6",
}

export const ASSETS_AUDIENCE_TYPE_BORDER_COLOR: Record<string, string> = {
    [ASSETS_AUDIENCE_TYPE.MANUAL]: "#1890FF1A",
    [ASSETS_AUDIENCE_TYPE.RULE]: "#FFCC021A",
}

// 用戶狀態：邀請中、使用中、已停用
export const ASSETS_STATE_ACCOUNT = {
    INITIAL: "initial",
    ACTIVE: "active",
    INACTIVE: "inactive",
} as const

export const ASSETS_STATE_ACCOUNT_CHINESE: Record<string, string> = {
    [ASSETS_STATE_ACCOUNT.INITIAL]: "邀請中",
    [ASSETS_STATE_ACCOUNT.ACTIVE]: "使用中",
    [ASSETS_STATE_ACCOUNT.INACTIVE]: "已停用",
}

export const ASSETS_STATE_ACCOUNT_COLOR: Record<string, string> = {
    [ASSETS_STATE_ACCOUNT.INITIAL]: "#FFE941",
    [ASSETS_STATE_ACCOUNT.ACTIVE]: "#3785FA",
    [ASSETS_STATE_ACCOUNT.INACTIVE]: "#FF4747",
}

// 連結選項：外開連結、內開連結
export const LINK_OPTIONS = {
    IN_APP: "in_app",
    INWARD: "inward",
    OUTWARD: "outward",
} as const

export const LINK_OPTIONS_CHINESE: Record<string, string> = {
    [LINK_OPTIONS.IN_APP]: "APP指定位置",
    [LINK_OPTIONS.OUTWARD]: "外開連結",
    [LINK_OPTIONS.INWARD]: "內開連結",
}

// 受眾分群類型：自由數量分群、自由比例分群
export const AUDIENCE_SPLIT_TYPE = {
    COUNT: "count",
    RATIO: "ratio",
} as const

export const AUDIENCE_SPLIT_TYPE_CHINESE: Record<string, string> = {
    [AUDIENCE_SPLIT_TYPE.COUNT]: "自由數量分群",
    [AUDIENCE_SPLIT_TYPE.RATIO]: "自由比例分群",
}

// 標籤狀態
export const TAGS_STATUS = {
    DRAFT: "draft",
    IN_USE: "in_use",
    DISABLED: "disabled",
    ARCHIVED: "archived",
} as const

// 貼標狀態
export const ATTACHED_TAGS_STATUS = {
    DRAFT: "draft",
    PROCESSING: "processing",
    IN_USE: "in_use",
    DISABLED: "disabled",
    ARCHIVED: "archived",
} as const

// 篩選貼標類型：固定貼標、條件貼標
export const ASSIGNER_TYPE = {
    MANUAL: "manual",
    RULE: "rule",
} as const

export const ASSIGNER_TYPE_BORDER_COLOR: Record<string, string> = {
    [ASSIGNER_TYPE.MANUAL]: "#150F961A",
    [ASSIGNER_TYPE.RULE]: "#FFCC021A",
}

// 上傳檔案狀態：完成、失敗、上傳中
export const UPLOAD_FILE_STATE = {
    SUCCESS: "success",
    FAIL: "failed",
    PROCESSING: "processing",
} as const

export const UPLOAD_FILE_STATE_CHINESE: Record<string, string> = {
    [UPLOAD_FILE_STATE.SUCCESS]: "完成",
    [UPLOAD_FILE_STATE.FAIL]: "失敗",
    [UPLOAD_FILE_STATE.PROCESSING]: "上傳中",
}

export const UPLOAD_FILE_STATE_COLOR: Record<string, string> = {
    [UPLOAD_FILE_STATE.SUCCESS]: "#52C41A",
    [UPLOAD_FILE_STATE.FAIL]: "#FF4D4F",
    [UPLOAD_FILE_STATE.PROCESSING]: "#1890FF",
}

// 上傳檔案類別：會員格式、訂單格式、名單貼標
export const UPLOAD_TYPE_STATE = {
    MEMBER_LIST: "member_list",
    ORDER_LIST: "order_list",
    TAG_LIST: "tag_list",
} as const

export const UPLOAD_TYPE_STATE_CHINESE: Record<string, string> = {
    [UPLOAD_TYPE_STATE.MEMBER_LIST]: "會員格式",
    [UPLOAD_TYPE_STATE.ORDER_LIST]: "訂單格式",
    [UPLOAD_TYPE_STATE.TAG_LIST]: "名單貼標",
}

// 篩選器上傳檔案類別
export const FILTER_UPLOAD_TYPE_STATE = {
    MEMBER_ID_LIST: "member_id_list",
    PHONE_LIST: "phone_list",
    EMAIL_LIST: "email_list",
} as const

export const FILTER_UPLOAD_TYPE_STATE_CHINESE: Record<string, string> = {
    [FILTER_UPLOAD_TYPE_STATE.MEMBER_ID_LIST]: "上傳名單-歸戶碼",
    [FILTER_UPLOAD_TYPE_STATE.PHONE_LIST]: "上傳名單-手機",
    [FILTER_UPLOAD_TYPE_STATE.EMAIL_LIST]: "上傳名單-郵件",
}

// 併買分析維度：全貢獻、指定商品、併買商品
export const ASSOCIATION_DIMENSION = {
    ALL: "0",
    SPECIFY: "1",
    ASSOCIATION: "2",
} as const

export const ASSOCIATION_DIMENSION_CHINESE: Record<string, string> = {
    [ASSOCIATION_DIMENSION.ALL]: "全貢獻",
    [ASSOCIATION_DIMENSION.SPECIFY]: "指定商品",
    [ASSOCIATION_DIMENSION.ASSOCIATION]: "併買商品",
}

// 群組分析天數中文：一、二、三、四、五、六、七
export const COHORT_DAYS = ["一", "二", "三", "四", "五", "六", "七"] as const

// 行銷活動狀態：草稿、待審核、審核中、排程中、寄送中、已取消、已結束、封存
const CAMPAIGN_STATE = {
    DRAFT: "draft",
    SCHEDULED: "available",
    SENDING: "sending",
    CANCELED: "canceled",
    ENDED: "ended",
    ARCHIVED: "archived",
    ...(doesSupportReview ? ({ REVIEWABLE: "reviewable", REVIEWING: "reviewing" } as const) : {}),
} as const

export { CAMPAIGN_STATE }

// 行銷活動狀態排序
const CAMPAIGN_STATE_ORDER: Record<string, number> = {
    [CAMPAIGN_STATE.DRAFT]: 1,
    [CAMPAIGN_STATE.SCHEDULED]: 4,
    [CAMPAIGN_STATE.SENDING]: 5,
    [CAMPAIGN_STATE.CANCELED]: 6,
    [CAMPAIGN_STATE.ENDED]: 7,
    ...(doesSupportReview ? { reviewable: 2, reviewing: 3 } : {}),
}

export { CAMPAIGN_STATE_ORDER }

// 行銷活動中文
const CAMPAIGN_STATE_CHINESE: Record<string, string> = {
    [CAMPAIGN_STATE.DRAFT]: "草稿",
    [CAMPAIGN_STATE.SCHEDULED]: "排程中",
    [CAMPAIGN_STATE.SENDING]: "寄送中",
    [CAMPAIGN_STATE.CANCELED]: "已取消",
    [CAMPAIGN_STATE.ENDED]: "已結束",
    ...(doesSupportReview ? { reviewable: "待審核", reviewing: "審核中" } : {}),
}

export { CAMPAIGN_STATE_CHINESE }

// 行銷活動顏色
const CAMPAIGN_STATE_COLOR: Record<string, string> = {
    [CAMPAIGN_STATE.DRAFT]: "#4F4F4F",
    [CAMPAIGN_STATE.SCHEDULED]: "#52C41A",
    [CAMPAIGN_STATE.SENDING]: "#317912",
    [CAMPAIGN_STATE.CANCELED]: "#6D6D6D",
    [CAMPAIGN_STATE.ENDED]: "#B0B0B0",
    ...(doesSupportReview ? { reviewable: "#FF751A", reviewing: "#FAC400" } : {}),
}

export { CAMPAIGN_STATE_COLOR }

// 行銷活動背景顏色
const CAMPAIGN_STATE_BG_COLOR: Record<string, string> = {
    [CAMPAIGN_STATE.DRAFT]: "#6D6D6D26",
    [CAMPAIGN_STATE.SCHEDULED]: "#52C41A0D",
    [CAMPAIGN_STATE.SENDING]: "#52C41A26",
    [CAMPAIGN_STATE.CANCELED]: "#6D6D6D1A",
    [CAMPAIGN_STATE.ENDED]: "#FAFAFA",
    ...(doesSupportReview ? { reviewable: "#FF751A0D", reviewing: "#FAC4000D" } : {}),
}

export { CAMPAIGN_STATE_BG_COLOR }

export const CAMPAIGN_TYPE = {
    ONCE: "once",
    REPEATING: "repeating",
    CONDITIONAL: "conditional",
} as const

export const CAMPAIGN_TYPE_CHINESE: Record<string, string> = {
    [CAMPAIGN_TYPE.ONCE]: "定時-單次活動",
    [CAMPAIGN_TYPE.REPEATING]: "定時-重複活動",
    [CAMPAIGN_TYPE.CONDITIONAL]: "條件式觸發",
}

export const CAMPAIGN_EDIT_MODE = {
    CREATE: "create",
    EDIT: "edit",
    REVIEW: "review",
} as const

export const CAMPAIGN_EDIT_MODE_CHINESE: Record<string, string> = {
    [CAMPAIGN_EDIT_MODE.CREATE]: "新增",
    [CAMPAIGN_EDIT_MODE.EDIT]: "編輯",
    [CAMPAIGN_EDIT_MODE.REVIEW]: "審核",
}

export const MEDIA_TYPE = {
    SMS: "sms",
    EDM: "edm",
    APP_NOTIFICATION: "APP notifications",
} as const

export const MEDIA_TYPE_CHINESE: Record<string, string> = {
    [MEDIA_TYPE.SMS]: "簡訊",
    [MEDIA_TYPE.EDM]: "EDM",
    [MEDIA_TYPE.APP_NOTIFICATION]: "APP推播",
}

// 行銷活動傳送狀態：傳送中、傳送成功、傳送失敗
export const CAMPAIGN_SEND_STATUS = {
    SENDING: "sending",
    SUCCESSFUL: "successful",
    FAILED: "failed",
} as const

export const CAMPAIGN_SEND_STATUS_CHINESE: Record<string, string> = {
    [CAMPAIGN_SEND_STATUS.SENDING]: "傳送中",
    [CAMPAIGN_SEND_STATUS.SUCCESSFUL]: "傳送成功",
    [CAMPAIGN_SEND_STATUS.FAILED]: "傳送失敗",
}

export const CAMPAIGN_SEND_STATUS_COLOR: Record<string, string> = {
    [CAMPAIGN_SEND_STATUS.SENDING]: "#FFE941",
    [CAMPAIGN_SEND_STATUS.SUCCESSFUL]: "#98EF67",
    [CAMPAIGN_SEND_STATUS.FAILED]: "#FF4747",
}

// 通道連線狀態：傳輸中、完成、失敗
export const CHANNEL_CONNECTION_STATUS = {
    CONNECTING: "connecting",
    SUCCESS: "success",
    FAILED: "failed",
} as const

// 審核狀態
export const REVIEW_STATUS = {
    REVIEWABLE: "reviewable",
    REVIEWING: "reviewing",
    PASS: "pass",
    REJECT: "reject",
    OVERTIME_REJECT: "overtime reject",
} as const
